import Phaser from 'phaser';
import { RunSimulation, type GateSnapshot, type SimulationEvent, type SimulationSnapshot, type TargetSnapshot } from '../../game/simulation';
import type { CaptainId, GameMode, RunModifiers, RunResult, Settings, StageDefinition } from '../../game/types';
import { getTargetAssetKey, RUN_ASSET_PATHS } from '../assets';

export interface RunLaunchOptions {
  stage: StageDefinition;
  mode: GameMode;
  captainId: CaptainId;
  modifiers: RunModifiers;
  settings: Settings;
}

interface BulletFx {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  x: number;
  y: number;
  previousX: number;
  previousY: number;
  progress: number;
  speed: number;
  curve: number;
  critical: boolean;
}

interface BurstFx {
  x: number;
  y: number;
  life: number;
  maxLife: number;
  color: number;
}

interface MuzzleFx {
  x: number;
  y: number;
  life: number;
  maxLife: number;
  angle: number;
}

interface ImpactFx {
  x: number;
  y: number;
  life: number;
  maxLife: number;
  color: number;
  size: number;
}

interface DamageFx {
  label: Phaser.GameObjects.Text;
  life: number;
  maxLife: number;
  driftX: number;
}

interface GameCommand {
  action: 'pause' | 'resume' | 'restart' | 'quit';
}

const WIDTH = 540;
const HEIGHT = 960;
const HORIZON_Y = 168;
const PLAYER_Y = 790;
const Z_SCALE = 5.25;

function colorNumber(value: string): number {
  return Number.parseInt(value.replace('#', ''), 16);
}

export class RunScene extends Phaser.Scene {
  private readonly options: RunLaunchOptions;
  private simulation!: RunSimulation;
  private graphics!: Phaser.GameObjects.Graphics;
  private vignette!: Phaser.GameObjects.Graphics;
  private gateLabels = new Map<string, Phaser.GameObjects.Text>();
  private bossLabel!: Phaser.GameObjects.Text;
  private targetSprites = new Map<number, Phaser.GameObjects.Image>();
  private troopSprites: Phaser.GameObjects.Image[] = [];
  private bullets: BulletFx[] = [];
  private bursts: BurstFx[] = [];
  private muzzles: MuzzleFx[] = [];
  private impacts: ImpactFx[] = [];
  private damageFx: DamageFx[] = [];
  private keys?: {
    left: Phaser.Input.Keyboard.Key;
    right: Phaser.Input.Keyboard.Key;
    a: Phaser.Input.Keyboard.Key;
    d: Phaser.Input.Keyboard.Key;
    escape: Phaser.Input.Keyboard.Key;
  };
  private lastHudUpdate = 0;
  private finishSent = false;
  private shakeTime = 0;
  private flash = 0;
  private commandHandler = (event: Event): void => this.handleCommand(event as CustomEvent<GameCommand>);
  private visibilityHandler = (): void => {
    if (document.hidden) this.setPaused(true);
  };

  constructor(options: RunLaunchOptions) {
    super({ key: 'RunScene' });
    this.options = options;
  }

  preload(): void {
    for (const [key, path] of Object.entries(RUN_ASSET_PATHS)) this.load.image(key, path);
  }

  create(): void {
    this.simulation = new RunSimulation({
      stage: this.options.stage,
      mode: this.options.mode,
      captainId: this.options.captainId,
      modifiers: this.options.modifiers,
    });
    this.graphics = this.add.graphics();
    this.vignette = this.add.graphics().setDepth(100);
    this.bossLabel = this.add
      .text(WIDTH / 2, 124, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        fontStyle: 'bold',
        color: '#fff2dc',
        backgroundColor: '#300f18cc',
        padding: { x: 14, y: 7 },
        align: 'center',
      })
      .setOrigin(0.5)
      .setDepth(30)
      .setVisible(false);

    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => this.setPointerTarget(pointer));
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (pointer.isDown) this.setPointerTarget(pointer);
    });

    if (this.input.keyboard) {
      this.keys = this.input.keyboard.addKeys({
        left: Phaser.Input.Keyboard.KeyCodes.LEFT,
        right: Phaser.Input.Keyboard.KeyCodes.RIGHT,
        a: Phaser.Input.Keyboard.KeyCodes.A,
        d: Phaser.Input.Keyboard.KeyCodes.D,
        escape: Phaser.Input.Keyboard.KeyCodes.ESC,
      }) as typeof this.keys;
    }

    window.addEventListener('breakline:command', this.commandHandler);
    document.addEventListener('visibilitychange', this.visibilityHandler);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.cleanup());
    this.events.once(Phaser.Scenes.Events.DESTROY, () => this.cleanup());
    this.dispatchHud(this.simulation.getSnapshot());
  }

  update(time: number, delta: number): void {
    const snapshotBefore = this.simulation.getSnapshot();
    if (this.keys && snapshotBefore.status === 'running') {
      const left = this.keys.left.isDown || this.keys.a.isDown;
      const right = this.keys.right.isDown || this.keys.d.isDown;
      if (left !== right) this.simulation.setTargetX(snapshotBefore.targetX + (right ? 1 : -1) * delta * 0.002);
      if (Phaser.Input.Keyboard.JustDown(this.keys.escape)) this.setPaused(true);
    }

    this.simulation.update(delta / 1000);
    const snapshot = this.simulation.getSnapshot();
    this.handleEvents(this.simulation.drainEvents(), snapshot);
    this.updateEffects(delta / 1000);
    this.draw(snapshot, time);

    if (time - this.lastHudUpdate > 80) {
      this.lastHudUpdate = time;
      this.dispatchHud(snapshot);
    }
  }

  private cleanup(): void {
    window.removeEventListener('breakline:command', this.commandHandler);
    document.removeEventListener('visibilitychange', this.visibilityHandler);
    this.targetSprites.clear();
    this.troopSprites = [];
    this.damageFx.forEach((effect) => effect.label.destroy());
    this.damageFx = [];
  }

  private handleCommand(event: CustomEvent<GameCommand>): void {
    if (event.detail.action === 'pause') this.setPaused(true);
    if (event.detail.action === 'resume') this.setPaused(false);
    if (event.detail.action === 'restart') {
      window.dispatchEvent(new CustomEvent('breakline:restart'));
    }
    if (event.detail.action === 'quit') {
      const result = this.simulation.quit();
      window.dispatchEvent(new CustomEvent<RunResult>('breakline:finish', { detail: result }));
    }
  }

  private setPaused(paused: boolean): void {
    this.simulation.setPaused(paused);
    window.dispatchEvent(new CustomEvent('breakline:pause', { detail: { paused } }));
  }

  private setPointerTarget(pointer: Phaser.Input.Pointer): void {
    this.simulation.setTargetX((pointer.x - WIDTH / 2) / (WIDTH * 0.4));
  }

  private handleEvents(events: SimulationEvent[], snapshot: SimulationSnapshot): void {
    for (const event of events) {
      if (event.type === 'shot') {
        const target = snapshot.targets.find((candidate) => candidate.id === event.targetId);
        const projected = target ? this.project(target.x, target.z, snapshot.distance) : { x: WIDTH / 2, y: HORIZON_Y };
        const volleySize = Math.min(7, 3 + Math.floor(snapshot.troops / 18));
        for (let index = 0; index < volleySize; index += 1) {
          const offset = (index - (volleySize - 1) / 2) * 10;
          const startX = this.worldX(event.fromX, 1) + offset;
          const startY = PLAYER_Y - 52 - (index % 2) * 7;
          const endX = projected.x + ((index * 13 + event.targetId * 7) % 19 - 9);
          const endY = projected.y + ((index * 11 + event.targetId) % 13 - 6);
          this.bullets.push({
            startX,
            startY,
            endX,
            endY,
            x: startX,
            y: startY,
            previousX: startX,
            previousY: startY,
            progress: -index * 0.035,
            speed: 3.35 + (index % 3) * 0.28,
            curve: offset * 0.24,
            critical: event.critical && index % 2 === 0,
          });
          this.muzzles.push({
            x: startX,
            y: startY,
            life: 0.1,
            maxLife: 0.1,
            angle: Phaser.Math.Angle.Between(startX, startY, endX, endY),
          });
        }
      }
      if (event.type === 'hit') {
        const target = snapshot.targets.find((candidate) => candidate.id === event.targetId);
        if (target) {
          const projected = this.project(target.x, target.z, snapshot.distance);
          const critical = event.amount > snapshot.troops * snapshot.damagePerUnit * 0.32;
          this.impacts.push({
            x: projected.x,
            y: projected.y,
            life: 0.34,
            maxLife: 0.34,
            color: critical ? 0xffd35f : 0xff8a3d,
            size: critical ? 30 : 22,
          });
          const label = this.add
            .text(projected.x, projected.y - 24, '-' + Math.max(1, Math.round(event.amount)), {
              fontFamily: 'Arial Black, system-ui, sans-serif',
              fontSize: critical ? '26px' : '20px',
              fontStyle: 'bold',
              color: critical ? '#fff29b' : '#ffffff',
              stroke: '#58170c',
              strokeThickness: 5,
            })
            .setOrigin(0.5)
            .setDepth(45);
          this.damageFx.push({ label, life: 0.62, maxLife: 0.62, driftX: ((event.targetId * 17) % 11) - 5 });
        }
      }
      if (event.type === 'destroy') {
        const projected = this.project(event.x, event.z, snapshot.distance);
        this.bursts.push({
          x: projected.x,
          y: projected.y,
          life: event.kind === 'boss' ? 1.05 : 0.68,
          maxLife: event.kind === 'boss' ? 1.05 : 0.68,
          color: event.kind === 'crate' ? 0x72dfff : 0xff6b2e,
        });
        this.shakeTime = this.options.settings.reducedMotion ? 0 : event.kind === 'boss' ? 0.32 : 0.12;
        this.dispatchToast(event.kind === 'crate' ? '補給物資 +5' : 'TARGET DOWN', 'positive');
      }
      if (event.type === 'hurt') {
        this.flash = event.shielded ? 0.55 : 0.3;
        this.shakeTime = this.options.settings.reducedMotion ? 0 : 0.16;
        this.dispatchToast(event.shielded ? '防衛シールド起動' : '兵士 −' + event.amount, event.shielded ? 'positive' : 'danger');
      }
      if (event.type === 'gate') {
        this.dispatchToast(event.option.label, event.option.positive ? 'positive' : 'danger');
      }
      if (event.type === 'support') {
        this.dispatchToast('支援ドローン +' + event.drones, 'positive');
      }
      if (event.type === 'boss') {
        this.bossLabel.setText('⚠ ' + this.options.stage.bossName).setVisible(true);
        this.dispatchToast('BOSS APPROACHING', 'danger');
      }
      if (event.type === 'finish' && !this.finishSent) {
        this.finishSent = true;
        this.time.delayedCall(this.options.settings.reducedMotion ? 150 : 850, () => {
          window.dispatchEvent(new CustomEvent<RunResult>('breakline:finish', { detail: event.result }));
        });
      }
    }
  }

  private dispatchToast(message: string, tone: 'positive' | 'danger'): void {
    window.dispatchEvent(new CustomEvent('breakline:toast', { detail: { message, tone } }));
  }

  private dispatchHud(snapshot: SimulationSnapshot): void {
    const boss = snapshot.targets.find((target) => target.kind === 'boss' && target.alive);
    window.dispatchEvent(
      new CustomEvent('breakline:hud', {
        detail: {
          troops: snapshot.troops,
          progress: snapshot.progress,
          damage: snapshot.damagePerUnit,
          rate: snapshot.fireRate,
          drones: snapshot.supportDrones,
          shield: snapshot.shieldReady,
          focus: snapshot.focusMultiplier,
          bossHp: boss && this.bossLabel.visible ? Math.max(0, boss.hp / boss.maxHp) : null,
        },
      }),
    );
  }

  private updateEffects(dt: number): void {
    this.bullets.forEach((bullet) => {
      const previousProgress = bullet.progress;
      bullet.progress += dt * bullet.speed;
      if (bullet.progress < 0) return;
      bullet.previousX = bullet.x;
      bullet.previousY = bullet.y;
      const point = this.bulletPoint(bullet, Phaser.Math.Clamp(bullet.progress, 0, 1));
      bullet.x = point.x;
      bullet.y = point.y;
      if (previousProgress < 1 && bullet.progress >= 1) {
        this.impacts.push({
          x: bullet.endX,
          y: bullet.endY,
          life: 0.22,
          maxLife: 0.22,
          color: bullet.critical ? 0xffdf70 : 0xff8a3d,
          size: bullet.critical ? 24 : 14,
        });
      }
    });
    this.bullets = this.bullets.filter((bullet) => bullet.progress < 1.05);
    this.bursts.forEach((burst) => {
      burst.life -= dt;
    });
    this.bursts = this.bursts.filter((burst) => burst.life > 0);
    this.muzzles.forEach((muzzle) => {
      muzzle.life -= dt;
    });
    this.muzzles = this.muzzles.filter((muzzle) => muzzle.life > 0);
    this.impacts.forEach((impact) => {
      impact.life -= dt;
    });
    this.impacts = this.impacts.filter((impact) => impact.life > 0);
    this.damageFx.forEach((effect) => {
      effect.life -= dt;
      effect.label.y -= dt * 46;
      effect.label.x += effect.driftX * dt;
      effect.label.setAlpha(Phaser.Math.Clamp(effect.life / (effect.maxLife * 0.55), 0, 1));
      effect.label.setScale(1 + (1 - effect.life / effect.maxLife) * 0.18);
      if (effect.life <= 0) effect.label.destroy();
    });
    this.damageFx = this.damageFx.filter((effect) => effect.life > 0);
    this.flash = Math.max(0, this.flash - dt * 1.8);
    this.shakeTime = Math.max(0, this.shakeTime - dt);
  }

  private bulletPoint(bullet: BulletFx, progress: number): { x: number; y: number } {
    const arc = Math.sin(progress * Math.PI);
    return {
      x: Phaser.Math.Linear(bullet.startX, bullet.endX, progress) + arc * bullet.curve,
      y: Phaser.Math.Linear(bullet.startY, bullet.endY, progress) - arc * 10,
    };
  }

  private draw(snapshot: SimulationSnapshot, time: number): void {
    const g = this.graphics;
    g.clear();
    this.drawBackdrop(g, snapshot, time);
    this.drawCourse(g, snapshot);
    this.drawGates(g, snapshot);
    this.drawTargets(g, snapshot, time);
    this.drawFormation(g, snapshot, time);
    this.drawEffects(g);
    this.drawVignette(snapshot);

    const shake = this.shakeTime > 0 ? Math.sin(time * 0.09) * 4 * (this.shakeTime / 0.16) : 0;
    this.cameras.main.setScroll(shake, 0);
  }

  private drawBackdrop(g: Phaser.GameObjects.Graphics, snapshot: SimulationSnapshot, time: number): void {
    const palette =
      snapshot.biome === 'canyon'
        ? { skyTop: 0x80b9ca, skyBottom: 0xf0c58e, ground: 0xb48555, haze: 0xffd6a0, city: 0x52646a }
        : snapshot.biome === 'industrial'
          ? { skyTop: 0x7899a4, skyBottom: 0xd8b08a, ground: 0x806d5c, haze: 0xffc58b, city: 0x46565b }
          : { skyTop: 0x7185a3, skyBottom: 0xc7a6a0, ground: 0x665f70, haze: 0xf4b39c, city: 0x3f485d };
    g.fillGradientStyle(palette.skyTop, palette.skyTop, palette.skyBottom, palette.skyBottom, 1);
    g.fillRect(0, 0, WIDTH, HEIGHT);

    g.fillStyle(palette.haze, 0.42);
    g.fillCircle(WIDTH * 0.76, 86, 55);
    g.fillStyle(0xffffff, 0.09);
    for (let i = 0; i < 5; i += 1) {
      const cloudX = ((i * 137 - snapshot.distance * 0.8) % (WIDTH + 180)) - 90;
      g.fillEllipse(cloudX, 54 + (i % 3) * 28, 150 + (i % 2) * 45, 18);
    }

    g.fillStyle(palette.ground, 0.72);
    g.fillRect(0, HORIZON_Y - 2, WIDTH, HEIGHT - HORIZON_Y + 2);
    g.fillStyle(0x6d7070, 0.24);
    for (let i = -1; i < 7; i += 1) {
      const x = i * 110 - ((snapshot.distance * 1.5) % 110);
      g.fillTriangle(x - 80, HORIZON_Y, x + 10, HORIZON_Y - 58 - (i % 3) * 16, x + 110, HORIZON_Y);
    }

    const shift = (snapshot.distance * 2.4) % 92;
    for (let i = -2; i < 9; i += 1) {
      const x = i * 86 - shift;
      const height = 34 + ((i * 31 + snapshot.stageId * 17) % 70);
      g.fillStyle(palette.city, 0.78);
      g.fillRect(x, HORIZON_Y - height, 66, height);
      g.fillTriangle(x - 5, HORIZON_Y - height, x + 18, HORIZON_Y - height - 24, x + 42, HORIZON_Y - height);
      g.fillStyle(0xffdd94, 0.28 + Math.sin(time * 0.001 + i) * 0.05);
      for (let windowY = HORIZON_Y - height + 12; windowY < HORIZON_Y - 8; windowY += 16) {
        g.fillRect(x + 10, windowY, 6, 4);
        g.fillRect(x + 28, windowY, 6, 4);
      }
    }
    g.fillGradientStyle(palette.haze, palette.haze, palette.ground, palette.ground, 0.28);
    g.fillRect(0, HORIZON_Y - 18, WIDTH, 44);
  }

  private drawCourse(g: Phaser.GameObjects.Graphics, snapshot: SimulationSnapshot): void {
    const road = [
      new Phaser.Geom.Point(188, HORIZON_Y),
      new Phaser.Geom.Point(352, HORIZON_Y),
      new Phaser.Geom.Point(604, HEIGHT),
      new Phaser.Geom.Point(-64, HEIGHT),
    ];
    g.fillGradientStyle(0x596065, 0x596065, 0x292f33, 0x292f33, 1);
    g.fillPoints(road, true);

    g.lineStyle(12, 0xd6c7aa, 0.92);
    g.lineBetween(188, HORIZON_Y, -64, HEIGHT);
    g.lineBetween(352, HORIZON_Y, 604, HEIGHT);
    g.lineStyle(3, 0xf2eee4, 0.9);
    g.lineBetween(192, HORIZON_Y, -50, HEIGHT);
    g.lineBetween(348, HORIZON_Y, 590, HEIGHT);

    const scroll = (snapshot.distance * 72) % 78;
    for (let y = HORIZON_Y + scroll; y < HEIGHT; y += 78) {
      const depth = Phaser.Math.Clamp((y - HORIZON_Y) / (HEIGHT - HORIZON_Y), 0, 1);
      const half = 74 + depth * 238;
      g.lineStyle(1 + depth * 1.5, 0x171b1d, 0.1 + depth * 0.13);
      g.lineBetween(WIDTH / 2 - half, y, WIDTH / 2 + half, y);
      g.lineStyle(1 + depth, 0xc8ced0, 0.08);
      g.lineBetween(WIDTH / 2 - half * 0.65, y + 4, WIDTH / 2 + half * 0.65, y + 4);
    }

    const dashScroll = (snapshot.distance * 92) % 112;
    for (const lane of [-0.48, 0.48]) {
      for (let y = HORIZON_Y - 30 + dashScroll; y < HEIGHT; y += 112) {
        const startY = Math.max(HORIZON_Y, y);
        const endY = Math.min(HEIGHT, y + 46);
        const startScale = this.depthScale(startY);
        const endScale = this.depthScale(endY);
        g.lineStyle(2 + endScale * 3, 0xf4f0dd, 0.7);
        g.lineBetween(this.worldX(lane, startScale), startY, this.worldX(lane, endScale), endY);
      }
    }

    g.lineStyle(3, 0x181c1e, 0.2);
    g.lineBetween(this.worldX(-0.12, 0.36), HORIZON_Y, this.worldX(-0.12, 1), HEIGHT);
    g.lineBetween(this.worldX(0.14, 0.36), HORIZON_Y, this.worldX(0.14, 1), HEIGHT);

    const propScroll = (snapshot.distance * 74) % 185;
    for (let i = 0; i < 6; i += 1) {
      const y = ((i * 185 + propScroll) % (HEIGHT - HORIZON_Y)) + HORIZON_Y;
      const depth = Phaser.Math.Clamp((y - HORIZON_Y) / (HEIGHT - HORIZON_Y), 0.08, 1);
      const side = i % 2 === 0 ? -1 : 1;
      this.drawRoadsideProp(g, this.worldX(side * 1.48, depth), y, depth, side, (i + snapshot.stageId) % 3);
    }

    const rush = (snapshot.distance * 126) % (HEIGHT - HORIZON_Y);
    for (let i = 0; i < 8; i += 1) {
      const y = ((i * 97 + rush) % (HEIGHT - HORIZON_Y)) + HORIZON_Y;
      const depth = Phaser.Math.Clamp((y - HORIZON_Y) / (HEIGHT - HORIZON_Y), 0.08, 1);
      const length = 14 + depth * 58;
      const inset = 18 + depth * 50;
      g.lineStyle(1 + depth * 2, 0xf8e4c3, 0.08 + depth * 0.13);
      g.lineBetween(inset, y - length, inset - depth * 14, y + 8);
      g.lineBetween(WIDTH - inset, y - length, WIDTH - inset + depth * 14, y + 8);
    }
  }

  private drawRoadsideProp(
    g: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    scale: number,
    side: number,
    variant: number,
  ): void {
    if (variant === 0) {
      const width = 82 * scale;
      const height = 36 * scale;
      g.fillStyle(0x242b2e, 0.92);
      g.fillRoundedRect(x - width / 2, y - height, width, height, 6 * scale);
      g.fillStyle(0x874832, 0.9);
      g.fillRoundedRect(x - width * 0.3, y - height * 1.35, width * 0.6, height * 0.55, 5 * scale);
      g.fillStyle(0x7eb1bd, 0.45);
      g.fillRect(x - width * 0.22, y - height * 1.25, width * 0.18, height * 0.28);
      g.fillRect(x + width * 0.04, y - height * 1.25, width * 0.18, height * 0.28);
      g.fillStyle(0x111416, 1);
      g.fillCircle(x - width * 0.3, y, 9 * scale);
      g.fillCircle(x + width * 0.3, y, 9 * scale);
      return;
    }
    if (variant === 1) {
      g.lineStyle(Math.max(1, 5 * scale), 0x403b34, 1);
      g.lineBetween(x, y, x + side * 4 * scale, y - 82 * scale);
      g.fillStyle(0xf3b545, 0.95);
      g.fillCircle(x + side * 4 * scale, y - 84 * scale, 8 * scale);
      g.fillStyle(0xffe6a3, 0.18);
      g.fillCircle(x + side * 4 * scale, y - 84 * scale, 22 * scale);
      return;
    }
    const width = 92 * scale;
    g.fillStyle(0x4e5352, 1);
    g.fillRect(x - width / 2, y - 24 * scale, width, 20 * scale);
    g.lineStyle(Math.max(1, 5 * scale), 0xe59b3a, 0.95);
    for (let index = -2; index <= 2; index += 1) {
      g.lineBetween(x + index * 20 * scale - 8 * scale, y - 4 * scale, x + index * 20 * scale + 8 * scale, y - 24 * scale);
    }
  }

  private drawGates(g: Phaser.GameObjects.Graphics, snapshot: SimulationSnapshot): void {
    const activeKeys = new Set<string>();
    const visibleGates = snapshot.gates
      .filter((gate) => !gate.resolved)
      .map((gate) => ({ gate, relative: gate.z - snapshot.distance }))
      .filter(({ relative }) => relative >= -2 && relative <= 116)
      .sort((a, b) => a.relative - b.relative);
    for (const { gate, relative } of visibleGates) {
      const previewSide = gate.id === visibleGates[0]?.gate.id && relative <= 24 ? (snapshot.playerX < 0 ? 'left' : 'right') : null;
      this.drawGateSide(g, gate, 'left', snapshot, activeKeys, previewSide);
      this.drawGateSide(g, gate, 'right', snapshot, activeKeys, previewSide);
    }
    for (const [key, label] of this.gateLabels) {
      label.setVisible(activeKeys.has(key));
    }
  }

  private drawGateSide(
    g: Phaser.GameObjects.Graphics,
    gate: GateSnapshot,
    side: 'left' | 'right',
    snapshot: SimulationSnapshot,
    activeKeys: Set<string>,
    previewSide: 'left' | 'right' | null,
  ): void {
    const option = gate[side];
    const projected = this.project(side === 'left' ? -0.56 : 0.56, gate.z, snapshot.distance);
    const scale = this.depthScale(projected.y);
    const width = 166 * scale;
    const height = 88 * scale;
    const color = option.positive ? 0x159ff2 : 0xe9384b;
    const selected = gate.selectedSide === side || (gate.selectedSide === null && previewSide === side);
    const rejected = (gate.selectedSide !== null || previewSide !== null) && !selected;
    const fillAlpha = selected ? 0.68 : rejected ? 0.12 : 0.48;
    const lineAlpha = selected ? 1 : rejected ? 0.28 : 0.86;
    g.fillStyle(0x34383a, rejected ? 0.3 : 0.9);
    g.fillRect(projected.x - width / 2 - 8 * scale, projected.y - height / 2 - 13 * scale, 7 * scale, height + 28 * scale);
    g.fillRect(projected.x + width / 2 + 1 * scale, projected.y - height / 2 - 13 * scale, 7 * scale, height + 28 * scale);
    g.fillStyle(color, rejected ? 0.14 : 0.9);
    g.fillRect(projected.x - width / 2 - 7 * scale, projected.y + height / 2 + 9 * scale, width + 14 * scale, 6 * scale);
    g.fillStyle(color, fillAlpha);
    g.fillRoundedRect(projected.x - width / 2, projected.y - height / 2, width, height, 8 * scale);
    g.lineStyle(Math.max(1, (selected ? 6 : 4) * scale), color, lineAlpha);
    g.strokeRoundedRect(projected.x - width / 2, projected.y - height / 2, width, height, 8 * scale);
    g.lineStyle(Math.max(1, 2 * scale), 0xffffff, rejected ? 0.1 : 0.58);
    g.lineBetween(projected.x - width / 2, projected.y + height / 2, projected.x + width / 2, projected.y + height / 2);

    const key = gate.id + '-' + side;
    activeKeys.add(key);
    let label = this.gateLabels.get(key);
    if (!label) {
      label = this.add
        .text(projected.x, projected.y, option.label, {
          fontFamily: 'Arial Black, system-ui, sans-serif',
          fontSize: '30px',
          fontStyle: 'bold',
          color: option.positive ? '#d8fbff' : '#ffe0e3',
          stroke: '#061018',
          strokeThickness: 5,
        })
        .setOrigin(0.5)
        .setDepth(20);
      this.gateLabels.set(key, label);
    }
    label
      .setPosition(projected.x, projected.y)
      .setScale(scale)
      .setAlpha(rejected ? 0.24 : 1)
      .setVisible(true)
      .setText(selected ? '◆ ' + option.label : option.label);
  }

  private drawTargets(g: Phaser.GameObjects.Graphics, snapshot: SimulationSnapshot, time: number): void {
    const activeSprites = new Set<number>();
    const visible = snapshot.targets
      .filter((target) => target.alive)
      .map((target) => ({ target, point: this.project(target.x, target.z, snapshot.distance) }))
      .filter(({ point }) => point.y > HORIZON_Y - 20 && point.y < HEIGHT + 70)
      .sort((a, b) => a.point.y - b.point.y);

    for (const { target, point } of visible) {
      const scale = this.depthScale(point.y);
      if (getTargetAssetKey(target.kind)) {
        this.updateTargetSprite(target, point.x, point.y, scale, time);
        activeSprites.add(target.id);
      }
      if (target.kind === 'boss') this.drawBoss(g, target, point.x, point.y, scale, time);
      else if (target.kind === 'barrier') this.drawBarrier(g, target, point.x, point.y, scale);
      else if (target.kind === 'crate') this.drawCrate(g, target, point.x, point.y, scale);
      else this.drawEnemy(g, target, point.x, point.y, scale, time);
    }

    for (const [id, sprite] of this.targetSprites) sprite.setVisible(activeSprites.has(id));
  }

  private updateTargetSprite(target: TargetSnapshot, x: number, y: number, scale: number, time: number): void {
    const key = getTargetAssetKey(target.kind);
    if (!key) return;
    let sprite = this.targetSprites.get(target.id);
    if (!sprite) {
      sprite = this.add.image(x, y, key).setOrigin(0.5, 0.975).setDepth(10);
      this.targetSprites.set(target.id, sprite);
    }

    const heights: Partial<Record<TargetSnapshot['kind'], number>> = {
      walker: 82,
      runner: 76,
      blocker: 112,
      spitter: 88,
      boss: 230,
    };
    const height = (heights[target.kind] ?? 82) * scale;
    const runRate = target.kind === 'boss' ? 0.004 : target.kind === 'blocker' ? 0.007 : 0.011;
    const phase = time * runRate + target.id * 0.73;
    const stride = Math.sin(phase);
    const pulse = target.kind === 'boss' ? 1 + Math.sin(time * 0.004) * 0.035 : 1;
    const baseScale = (height / 640) * pulse;
    const groundOffset = (target.kind === 'boss' ? 48 : target.kind === 'blocker' ? 31 : 23) * scale;

    sprite
      .setVisible(true)
      .setPosition(x + stride * 1.6 * scale, y + groundOffset + Math.abs(stride) * 2.2 * scale)
      .setScale(baseScale * (1 + Math.abs(stride) * 0.025), baseScale * (1 - Math.abs(stride) * 0.018))
      .setAngle(stride * (target.kind === 'boss' ? 0.7 : 2.2))
      .setDepth(8 + y / 150);

    if (target.kind === 'runner') sprite.setTint(0xffd7a0);
    else sprite.clearTint();
  }

  private drawEnemy(g: Phaser.GameObjects.Graphics, target: TargetSnapshot, x: number, y: number, scale: number, time: number): void {
    const baseHeight = target.kind === 'blocker' ? 112 : target.kind === 'spitter' ? 88 : target.kind === 'runner' ? 76 : 82;
    const height = baseHeight * scale;
    const groundY = y + (target.kind === 'blocker' ? 31 : 23) * scale;
    const stride = Math.sin(time * 0.011 + target.id * 0.73);
    g.fillStyle(0x000000, 0.36);
    g.fillEllipse(x, groundY + 2 * scale, height * 0.55, height * 0.13);
    if (target.kind === 'runner') {
      g.lineStyle(Math.max(1, 3 * scale), 0xffc16c, 0.32);
      g.lineBetween(x - 10 * scale, groundY, x - 28 * scale - stride * 8, groundY + 12 * scale);
      g.lineBetween(x + 7 * scale, groundY, x - 12 * scale - stride * 5, groundY + 17 * scale);
    }
    if (target.kind === 'spitter') {
      g.fillStyle(0xbf69ff, 0.12 + Math.sin(time * 0.006) * 0.04);
      g.fillCircle(x, groundY - height * 0.56, height * 0.36);
    }
    this.drawHealth(g, target, x, groundY - height - 7 * scale, Math.max(34 * scale, height * 0.52));
  }

  private drawBarrier(g: Phaser.GameObjects.Graphics, target: TargetSnapshot, x: number, y: number, scale: number): void {
    const width = 112 * scale;
    const height = 48 * scale;
    g.fillStyle(0x1b2226, 1);
    g.fillRoundedRect(x - width / 2, y - height / 2, width, height, 5 * scale);
    g.lineStyle(4 * scale, 0xff7159, 0.88);
    g.strokeRoundedRect(x - width / 2, y - height / 2, width, height, 5 * scale);
    g.lineStyle(7 * scale, 0xffa451, 0.8);
    for (let i = -2; i <= 2; i += 1) {
      g.lineBetween(x + i * 22 * scale - 12 * scale, y + height / 2, x + i * 22 * scale + 12 * scale, y - height / 2);
    }
    this.drawHealth(g, target, x, y - height * 0.86, width * 0.82);
  }

  private drawCrate(g: Phaser.GameObjects.Graphics, target: TargetSnapshot, x: number, y: number, scale: number): void {
    const size = 46 * scale;
    g.fillStyle(0x123c48, 1);
    g.fillRoundedRect(x - size / 2, y - size / 2, size, size, 7 * scale);
    g.lineStyle(4 * scale, 0x6df4ff, 0.95);
    g.strokeRoundedRect(x - size / 2, y - size / 2, size, size, 7 * scale);
    g.lineBetween(x - size * 0.24, y, x + size * 0.24, y);
    g.lineBetween(x, y - size * 0.24, x, y + size * 0.24);
    this.drawHealth(g, target, x, y - size * 0.75, size);
  }

  private drawBoss(g: Phaser.GameObjects.Graphics, target: TargetSnapshot, x: number, y: number, scale: number, time: number): void {
    const pulse = 1 + Math.sin(time * 0.005) * 0.04;
    const height = 230 * scale * pulse;
    const groundY = y + 48 * scale;
    g.fillStyle(0xff4b61, 0.09);
    g.fillCircle(x, groundY - height * 0.52, height * 0.58);
    g.lineStyle(Math.max(1, 4 * scale), 0xff6174, 0.24 + Math.sin(time * 0.006) * 0.08);
    g.strokeCircle(x, groundY - height * 0.52, height * 0.5);
    g.fillStyle(0x05080d, 0.5);
    g.fillEllipse(x, groundY + 4 * scale, height * 0.72, height * 0.14);
    this.drawHealth(g, target, x, groundY - height - 10 * scale, Math.min(280, height * 0.92));
  }

  private drawHealth(g: Phaser.GameObjects.Graphics, target: TargetSnapshot, x: number, y: number, width: number): void {
    const ratio = Phaser.Math.Clamp(target.hp / target.maxHp, 0, 1);
    g.fillStyle(0x000000, 0.62);
    g.fillRoundedRect(x - width / 2, y, width, 6, 3);
    g.fillStyle(ratio > 0.45 ? 0x68ebaf : 0xff6875, 1);
    g.fillRoundedRect(x - width / 2 + 1, y + 1, Math.max(0, (width - 2) * ratio), 4, 2);
  }

  private drawFormation(g: Phaser.GameObjects.Graphics, snapshot: SimulationSnapshot, time: number): void {
    const count = Math.min(snapshot.troops, 20);
    const columns = Math.min(5, Math.max(3, Math.ceil(Math.sqrt(count * 1.2))));
    const rows = Math.ceil(count / columns);
    const centerX = this.worldX(snapshot.playerX, 1);

    while (this.troopSprites.length < count) {
      this.troopSprites.push(this.add.image(centerX, PLAYER_Y, 'ranger').setOrigin(0.5, 0.975).setDepth(20));
    }
    this.troopSprites.forEach((sprite, index) => sprite.setVisible(index < count));

    g.fillStyle(colorNumber(snapshot.accent), 0.08);
    g.fillEllipse(centerX, PLAYER_Y + 24, 156 + columns * 8, 68 + rows * 6);
    g.lineStyle(2, colorNumber(snapshot.accent), 0.25);
    g.strokeEllipse(centerX, PLAYER_Y + 24, 156 + columns * 8, 68 + rows * 6);

    for (let index = count - 1; index >= 0; index -= 1) {
      const row = Math.floor(index / columns);
      const column = index % columns;
      const rowCount = Math.min(columns, count - row * columns);
      const x = centerX + (column - (rowCount - 1) / 2) * 28 + (row % 2) * 7;
      const stride = Math.sin(time * 0.014 + index * 0.9);
      const y = PLAYER_Y + row * 24 - rows * 10 + Math.abs(stride) * 2;
      const captain = index === 0;
      const height = captain ? 68 : 52;
      const baseScale = height / 640;
      const sprite = this.troopSprites[index];
      g.fillStyle(0x000000, 0.35);
      g.fillEllipse(x, y + 10, captain ? 30 : 22, captain ? 9 : 7);
      sprite
        .setPosition(x + stride * 0.8, y + 11)
        .setScale(baseScale * (1 + Math.abs(stride) * 0.035), baseScale * (1 - Math.abs(stride) * 0.025))
        .setAngle(stride * 2.4)
        .setDepth(20 + row * 0.1 + (captain ? 1 : 0));
      sprite.clearTint().setAlpha(captain ? 1 : 0.92);
      if (captain) {
        g.lineStyle(2, colorNumber(snapshot.accent), 0.7);
        g.strokeCircle(x, y - 13, 18);
      }
    }

    for (let i = 0; i < snapshot.supportDrones; i += 1) {
      const angle = time * 0.002 + (i / Math.max(1, snapshot.supportDrones)) * Math.PI * 2;
      const x = centerX + Math.cos(angle) * 88;
      const y = PLAYER_Y - 25 + Math.sin(angle) * 22;
      g.fillStyle(0x8ff7ff, 0.18);
      g.fillCircle(x, y, 14);
      g.fillStyle(0x8ff7ff, 1);
      g.fillTriangle(x, y - 7, x - 10, y + 7, x + 10, y + 7);
    }
  }

  private drawEffects(g: Phaser.GameObjects.Graphics): void {
    for (const bullet of this.bullets) {
      if (bullet.progress < 0) continue;
      const headT = Phaser.Math.Clamp(bullet.progress, 0, 1);
      const tailT = Phaser.Math.Clamp(headT - (bullet.critical ? 0.26 : 0.18), 0, 1);
      const tail = this.bulletPoint(bullet, tailT);
      const color = bullet.critical ? 0xffcf4d : 0xff8b35;
      const alpha = Phaser.Math.Clamp(1 - Math.max(0, bullet.progress - 0.88) * 5, 0, 1);
      g.lineStyle(bullet.critical ? 12 : 8, color, alpha * 0.16);
      g.lineBetween(tail.x, tail.y, bullet.x, bullet.y);
      g.lineStyle(bullet.critical ? 5 : 3, 0xfff3c2, alpha * 0.95);
      g.lineBetween(tail.x, tail.y, bullet.x, bullet.y);
      g.fillStyle(color, alpha * 0.26);
      g.fillCircle(bullet.x, bullet.y, bullet.critical ? 11 : 7);
      g.fillStyle(0xffffff, alpha);
      g.fillCircle(bullet.x, bullet.y, bullet.critical ? 4 : 2.5);
    }
    for (const muzzle of this.muzzles) {
      const ratio = Phaser.Math.Clamp(muzzle.life / muzzle.maxLife, 0, 1);
      const length = 24 * ratio;
      g.fillStyle(0xffb32f, ratio * 0.28);
      g.fillCircle(muzzle.x, muzzle.y, 20 * ratio);
      g.fillStyle(0xfff1a6, ratio);
      g.fillTriangle(
        muzzle.x + Math.cos(muzzle.angle) * length,
        muzzle.y + Math.sin(muzzle.angle) * length,
        muzzle.x + Math.cos(muzzle.angle + 1.9) * 7 * ratio,
        muzzle.y + Math.sin(muzzle.angle + 1.9) * 7 * ratio,
        muzzle.x + Math.cos(muzzle.angle - 1.9) * 7 * ratio,
        muzzle.y + Math.sin(muzzle.angle - 1.9) * 7 * ratio,
      );
    }
    for (const impact of this.impacts) {
      const ratio = Phaser.Math.Clamp(impact.life / impact.maxLife, 0, 1);
      const progress = 1 - ratio;
      g.fillStyle(impact.color, ratio * 0.32);
      g.fillCircle(impact.x, impact.y, impact.size * (0.6 + progress));
      g.lineStyle(2 + ratio * 2, 0xfff2be, ratio);
      g.strokeCircle(impact.x, impact.y, impact.size * progress);
      for (let index = 0; index < 7; index += 1) {
        const angle = (index / 7) * Math.PI * 2 + impact.x * 0.01;
        const inner = impact.size * progress * 0.35;
        const outer = impact.size * (0.55 + progress);
        g.lineStyle(2, index % 2 === 0 ? 0xffffff : impact.color, ratio);
        g.lineBetween(
          impact.x + Math.cos(angle) * inner,
          impact.y + Math.sin(angle) * inner,
          impact.x + Math.cos(angle) * outer,
          impact.y + Math.sin(angle) * outer,
        );
      }
    }
    for (const burst of this.bursts) {
      const ratio = Phaser.Math.Clamp(burst.life / burst.maxLife, 0, 1);
      const progress = 1 - ratio;
      g.fillStyle(0x282b2b, ratio * 0.42);
      g.fillCircle(burst.x - 10, burst.y - progress * 46, 18 + progress * 26);
      g.fillCircle(burst.x + 14, burst.y - progress * 32, 13 + progress * 20);
      g.fillStyle(burst.color, ratio * 0.46);
      g.fillCircle(burst.x, burst.y, 18 + progress * 34);
      g.fillStyle(0xffd35a, ratio * 0.72);
      g.fillCircle(burst.x, burst.y, 10 + progress * 18);
      for (let i = 0; i < 12; i += 1) {
        const angle = (i / 12) * Math.PI * 2;
        const radius = progress * 72;
        g.fillStyle(i % 2 === 0 ? 0xffe27a : burst.color, ratio * 0.9);
        g.fillCircle(burst.x + Math.cos(angle) * radius, burst.y + Math.sin(angle) * radius, 2 + ratio * 4);
      }
    }
  }

  private drawVignette(snapshot: SimulationSnapshot): void {
    const g = this.vignette;
    g.clear();
    g.fillStyle(0x000000, 0.24);
    g.fillRect(0, 0, WIDTH, 20);
    g.fillRect(0, HEIGHT - 32, WIDTH, 32);
    g.fillStyle(snapshot.troops < 8 ? 0xff253f : 0xffd07a, snapshot.troops < 8 ? 0.08 + Math.sin(snapshot.elapsed * 7) * 0.04 : 0);
    g.fillRect(0, 0, WIDTH, HEIGHT);
    if (this.flash > 0) {
      g.fillStyle(snapshot.shieldReady ? 0x7af4ff : 0xff5368, this.flash * 0.45);
      g.fillRect(0, 0, WIDTH, HEIGHT);
    }
  }

  private project(x: number, z: number, distance: number): { x: number; y: number } {
    const relative = z - distance;
    const y = PLAYER_Y - relative * Z_SCALE;
    return { x: this.worldX(x, this.depthScale(y)), y };
  }

  private worldX(x: number, scale: number): number {
    return WIDTH / 2 + x * 238 * Phaser.Math.Clamp(scale, 0.38, 1);
  }

  private depthScale(y: number): number {
    return Phaser.Math.Clamp(0.34 + ((y - HORIZON_Y) / (PLAYER_Y - HORIZON_Y)) * 0.66, 0.3, 1.15);
  }
}
